// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage SALES-PROFORMA-APPROVAL-1 · pure frontend helpers.
//
// Label / colour maps + button-visibility logic for the L1/L2/L3 approval
// workflow. No React, no network — unit-testable in isolation (mirrors the
// project's proformaHelpers convention). The enums mirror the backend
// proforma.constants approval section.
// ════════════════════════════════════════════════════════════════════

import { hasPermission } from './proformaHelpers.js';

export const PERM = Object.freeze({
  STATUS_REQUEST:   'erp.sales.proforma.status.request',
  L1_APPROVE:       'erp.sales.proforma.l1.approve',
  ACCOUNTS_APPROVE: 'erp.sales.proforma.accounts.approve',
  L3_APPROVE:       'erp.sales.proforma.l3.approve',
  APPROVAL_VIEW:    'erp.sales.proforma.approval.view'
});

export const REQUESTED_ACTION = Object.freeze({
  CONFIRM: 'CONFIRM_PROFORMA',
  HOLD:    'HOLD_PROFORMA',
  CANCEL:  'CANCEL_PROFORMA'
});

const APPROVAL_STATUS_LABEL = {
  NONE: '—',
  L1_PENDING: 'L1 Approval Pending',
  L1_APPROVED: 'L1 Approved',
  L1_REJECTED: 'L1 Rejected',
  ACCOUNTS_PENDING: 'Accounts Approval Pending',
  ACCOUNTS_APPROVED: 'Accounts Approved',
  ACCOUNTS_REJECTED: 'Accounts Rejected',
  ACCOUNTS_HOLD: 'Accounts Hold',
  // L3 is presented to users as "Management Approval" (the internal enum stays L3);
  // approved/rejected forms mirror the Accounts pattern above.
  L3_PENDING: 'Management Approval Pending',
  L3_APPROVED: 'Management Approved',
  L3_REJECTED: 'Management Rejected'
};

export function approvalStatusLabel(s) {
  if (!s) return '—';
  return APPROVAL_STATUS_LABEL[s] || String(s);
}

// AntD Tag colour by approval status family.
export function approvalStatusColor(s) {
  if (!s || s === 'NONE') return 'default';
  if (/_APPROVED$/.test(s)) return 'green';
  if (/_REJECTED$/.test(s)) return 'red';
  if (/_HOLD$/.test(s)) return 'orange';
  if (/_PENDING$/.test(s)) return 'gold';
  return 'default';
}

const REQUESTED_ACTION_LABEL = {
  CONFIRM_PROFORMA: 'Confirm',
  HOLD_PROFORMA: 'Hold',
  CANCEL_PROFORMA: 'Cancel',
  ACCOUNTS_APPROVE_PROFORMA: 'Accounts Approval',
  ESCALATE_CREDIT_OVERRIDE: 'Escalate for Approval',
  APPROVE_CREDIT_OVERRIDE: 'Management Approval'
};
export function requestedActionLabel(a) { return REQUESTED_ACTION_LABEL[a] || (a == null ? '—' : String(a)); }

// Friendly display label for an approval level (internal codes stay L1/L2/L3).
// The inbox carries L2 (Accounts) + L3 (Management Approval) work.
const APPROVAL_LEVEL_LABEL = { L1: 'Sales', L2: 'Accounts', L3: 'Management' };
export function approvalLevelLabel(level) { return APPROVAL_LEVEL_LABEL[level] || (level == null ? '—' : String(level)); }

// Pure mirror of the backend canReleaseWorkOrderFromHeader (Stage APPROVAL-2:
// mode-agnostic — WO readiness is the authoritative flag, set only when the
// tenant's approval level, whatever it is, is fully satisfied).
export function canReleaseWorkOrder(header) {
  if (!header) return false;
  return header.status === 'CONFIRMED' && header.woReleaseStatus === 'WO_READY';
}

// Work-Order readiness display for the editor's compact panel.
// `header` carries { status, approvalStatus, woReleaseStatus }.
export function woReadinessView(header) {
  const status = header && header.status;
  if (status !== 'CONFIRMED') {
    return { label: 'Not available', color: 'default', ready: false, note: 'Available after confirmation.' };
  }
  if (canReleaseWorkOrder(header)) {
    if (header.approvalStatus === 'L3_APPROVED') {
      return { label: 'Ready by Management Approval', color: 'green', ready: true, note: 'Management approval granted.' };
    }
    return { label: 'Ready', color: 'green', ready: true, note: 'Accounts approved.' };
  }
  return { label: 'Blocked', color: 'gold', ready: false, note: 'Accounts / credit approval pending.' };
}

// Which requested_action strings currently sit in a non-terminal state.
export function pendingActionSet(approvals) {
  const set = new Set();
  const list = Array.isArray(approvals) ? approvals : [];
  for (const a of list) {
    if (a && (a.status === 'PENDING' || a.status === 'HELD')) set.add(a.requestedAction);
  }
  return set;
}

// Editor: which "Request …" buttons to show. Requests can only be raised from a
// SAVED, PENDING proforma by a user with the request permission, and only while
// no L1 request is already pending.
export function proformaRequestActions(header, permissions, pendingActions) {
  const status = header && header.status;
  const isNew = !(header && header.id);
  const canReq = hasPermission(permissions, PERM.STATUS_REQUEST);
  const pend = pendingActions instanceof Set ? pendingActions : new Set(pendingActions || []);
  const l1Pending = pend.has(REQUESTED_ACTION.CONFIRM) || pend.has(REQUESTED_ACTION.HOLD) || pend.has(REQUESTED_ACTION.CANCEL);
  const base = !isNew && status === 'PENDING' && canReq && !l1Pending;
  return {
    requestConfirm: base,
    requestHold: base,
    requestCancel: base,
    l1Pending: !isNew && status === 'PENDING' && l1Pending
  };
}

// Inbox: approve/reject/hold/escalate buttons for one request row, by level +
// permission + request state.
export function approverActionsFor(request, permissions) {
  const none = { approve: false, reject: false, hold: false, escalate: false };
  if (!request) return none;
  const open = request.status === 'PENDING' || request.status === 'HELD';
  if (!open) return none;
  // L1 (PROFORMA_STATUS_CHANGE) is decided on the list drawer now, not here — the
  // inbox only carries Accounts (L2) and credit-override (L3) reviews.
  if (request.approvalType === 'ACCOUNTS_APPROVAL') {
    const can = hasPermission(permissions, PERM.ACCOUNTS_APPROVE);
    return { approve: can, reject: can, hold: can && request.status === 'PENDING', escalate: can };
  }
  if (request.approvalType === 'CREDIT_OVERRIDE' && request.status === 'PENDING') {
    const can = hasPermission(permissions, PERM.L3_APPROVE);
    return { approve: can, reject: can, hold: false, escalate: false };
  }
  return none;
}

// Level accent (consistent across the inbox so an approver scans by level).
export function approvalLevelColor(level) {
  if (level === 'L1') return 'geekblue';
  if (level === 'L2') return 'gold';
  if (level === 'L3') return 'volcano';
  return 'default';
}

// Inbox tabs → server filter. `countKey` maps to the /inbox/counts response so a
// tab can show its live badge. The Accounts tab includes HELD (a held accounts
// review is still open work), so it never disappears from the approver's queue.
// L1 (status change) is decided on the list drawer, so it is NOT an inbox tab —
// this queue is Accounts (L2) + credit-override (L3) only.
export const APPROVAL_TABS = Object.freeze([
  { key: 'ACCOUNTS',  label: 'Accounts',            countKey: 'ACCOUNTS', filter: { type: 'ACCOUNTS_APPROVAL', status: ['PENDING', 'HELD'] } },
  { key: 'L3',        label: 'Management Approval', countKey: 'L3',       filter: { level: 'L3', status: 'PENDING' } },
  { key: 'APPROVED',  label: 'Approved',         countKey: null,       filter: { status: 'APPROVED' } },
  { key: 'REJECTED',  label: 'Rejected',         countKey: null,       filter: { status: 'REJECTED' } },
  { key: 'ALL',       label: 'All',              countKey: null,       filter: {} }
]);

export function inboxFilterForTab(tabKey) {
  const tab = APPROVAL_TABS.find((t) => t.key === tabKey);
  return tab ? { ...tab.filter } : {};
}

// Confirmation-mode options for the "Request Confirm" modal.
export const CONFIRMATION_MODES = Object.freeze(['Phone', 'WhatsApp', 'Email', 'Verbal', 'Written PO']);
